import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { IconEllipsis } from './icons'

// The window's "more" menu, opened by an ellipsis button beside the title. It
// renders whatever feature items (edit/delete/...) the consumer supplies. If
// there are none, the trigger hides itself so the header stays clean.
function WindowMenu({ extraItems }) {
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState(null)
  const btnRef = useRef(null)
  const menuRef = useRef(null)

  const place = useCallback(() => {
    const rect = btnRef.current?.getBoundingClientRect()
    if (rect) setPos({ top: rect.bottom + 4, left: rect.left })
  }, [])

  useLayoutEffect(() => {
    if (!open) return undefined
    place()
    const handler = () => place()
    window.addEventListener('resize', handler)
    window.addEventListener('scroll', handler, true)
    return () => {
      window.removeEventListener('resize', handler)
      window.removeEventListener('scroll', handler, true)
    }
  }, [open, place])

  useEffect(() => {
    if (!open) return undefined
    const onDown = (event) => {
      if (menuRef.current?.contains(event.target)) return
      if (btnRef.current?.contains(event.target)) return
      setOpen(false)
    }
    const onKey = (event) => event.key === 'Escape' && setOpen(false)
    document.addEventListener('mousedown', onDown, true)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown, true)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  if (extraItems == null) return null

  const menu = pos && (
    <div
      className="grid-move-menu"
      ref={menuRef}
      style={{ top: pos.top, left: pos.left }}
      role="menu"
      onClick={() => setOpen(false)}
    >
      {extraItems}
    </div>
  )

  return (
    <>
      <button
        type="button"
        ref={btnRef}
        title="More"
        aria-label="More"
        aria-haspopup="menu"
        aria-expanded={open}
        className={'grid-window-menu-btn' + (open ? ' is-active' : '')}
        onClick={() => setOpen((v) => !v)}
      >
        <IconEllipsis />
      </button>
      {open && menu && createPortal(menu, document.body)}
    </>
  )
}

export default WindowMenu
